"use client";

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import gsap from "gsap";
import CustomEase from "gsap/dist/CustomEase";
import { RiMenu3Line, RiCloseLine, RiArrowDownSLine } from "react-icons/ri";
import { Link } from "@/i18n/routing";
import Letstalk from "../header/LetsTalk";
import type { ComponentProps } from "react";

type Href = ComponentProps<typeof Link>["href"];

type NavItem = { label: string; href: Href };

type NavLink =
  | { label: string; type: "dropdown"; items: NavItem[] }
  | { label: string; type?: "link"; href: Href };

const services = [
  { label: "UI/UX design", href: "/ui-ux" },
  { label: "Web development and SEO", href: "/web-seo" },
  { label: "Testing and Consulting", href: "/testing-consulting" },
  { label: "Maintenance & Support", href: "/maintenance-support" },
] satisfies NavItem[];

const industries = [
  { label: "Business & Professional", href: "/business-professional" },
  { label: "Commerce & Hospitality", href: "/Commerce-Hospitality" },
  { label: "Technology & Digital", href: "/Technology-Digital" },
  { label: "Property & Construction", href: "/Property-Construction" },
  { label: "Health & Lifestyle", href: "/Health-Lifestyle" },
  { label: "Education & Public", href: "/Education-Public" },
] satisfies NavItem[];

const NAV_LINKS = [
  { label: "Services", type: "dropdown", items: services },
  { label: "Case Studies", href: "/studies" },
  { label: "Industry", type: "dropdown", items: industries },
  { label: "Digital Trends", href: "/trends" },
  { label: "About", href: "/about-us" },
  { label: "Contact", href: "/contact-us" },
] satisfies NavLink[];

const EASE = "menuEase";

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const getItems = (root: HTMLElement | null) =>
  root ? Array.from(root.querySelectorAll<HTMLElement>(".menu-link-item")) : [];

const labelClasses =
  "text-3xl font-semibold uppercase leading-[1.15] text-[var(--copy)] min-[400px]:text-4xl sm:text-5xl md:text-6xl";

/* ---------- Menu list (memoized so state changes don't re-render it needlessly) ---------- */

type MenuListProps = {
  openSection: string | null;
  onToggleSection: (label: string) => void;
  onLinkClick: () => void;
};

const MenuList = memo(function MenuList({
  openSection,
  onToggleSection,
  onLinkClick,
}: MenuListProps) {
  return (
    <div className="flex max-h-[80dvh] w-full max-w-xl flex-col items-center justify-center gap-1 overflow-y-auto overscroll-contain px-4 py-4 text-center sm:gap-2 sm:px-6">
      {NAV_LINKS.map((link) => {
        const isSectionOpen = openSection === link.label;

        return (
          <div
            key={link.label}
            className="menu-link relative flex w-full flex-col items-center overflow-hidden"
          >
            {link.type === "dropdown" ? (
              <>
                <button
                  type="button"
                  onClick={() => onToggleSection(link.label)}
                  aria-expanded={isSectionOpen}
                  className="menu-link-item relative inline-flex items-center gap-2"
                >
                  <span className={labelClasses}>{link.label}</span>
                  <RiArrowDownSLine
                    className={`h-6 w-6 text-[var(--copy)] transition-transform duration-300 sm:h-7 sm:w-7 ${
                      isSectionOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid w-full transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(.76,0,.2,1)] ${
                    isSectionOpen
                      ? "mt-1 grid-rows-[1fr] opacity-100"
                      : "mt-0 grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <ul className="flex flex-col items-center gap-0.5 overflow-hidden py-2">
                    {link.items.map((item) => (
                      <li key={item.label}>
                        <Link
                          href={item.href}
                          onClick={onLinkClick}
                          tabIndex={isSectionOpen ? 0 : -1}
                          className="block py-1.5 text-sm text-[var(--copy)]/70 transition-colors hover:text-[var(--copy)] min-[400px]:text-base sm:text-2xl"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <Link
                href={link.href}
                onClick={onLinkClick}
                className="menu-link-item relative inline-block"
              >
                <span className={labelClasses}>{link.label}</span>
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
});

/* ---------- Nav ---------- */

const Nav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);

  const overlayRef = useRef<HTMLDivElement>(null);
  const openIconRef = useRef<HTMLSpanElement>(null);
  const closeIconRef = useRef<HTMLSpanElement>(null);
  const animating = useRef(false);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  // Put everything in its closed state (no animation)
  const resetClosed = useCallback(() => {
    gsap.set(overlayRef.current, { yPercent: 100, visibility: "hidden" });
    gsap.set(getItems(overlayRef.current), { yPercent: 100 });
    gsap.set(closeIconRef.current, { yPercent: 100, autoAlpha: 1 });
    gsap.set(openIconRef.current, { yPercent: 0 });
  }, []);

  // Initial state is set before first paint, so there's no flash
  useIsoLayoutEffect(() => {
    gsap.registerPlugin(CustomEase);
    CustomEase.create(EASE, ".76,0,.2,1");
    resetClosed();

    return () => {
      tlRef.current?.kill();
    };
  }, [resetClosed]);

  const handleOpenMenu = useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay || animating.current) return;
    animating.current = true;

    setIsOpen(true);

    tlRef.current = gsap
      .timeline({
        onComplete: () => {
          animating.current = false;
        },
      })
      .set(overlay, { visibility: "visible" })
      .to(overlay, { yPercent: 0, duration: 0.85, ease: EASE }, 0)
      .to(openIconRef.current, { yPercent: -100, duration: 0.6, ease: EASE }, 0)
      .set(openIconRef.current, { yPercent: 100 }, 0.6)
      .to(closeIconRef.current, { yPercent: 0, duration: 0.6, ease: EASE }, 0.15)
      .to(
        getItems(overlay),
        { yPercent: 0, duration: 0.8, stagger: 0.07, ease: "power3.out" },
        0.35
      );
  }, []);

  // fast = true when a link was clicked: the page is changing anyway,
  // so we close quicker to keep the main thread free for the route change
  const handleCloseMenu = useCallback(
    (fast = false) => {
      const overlay = overlayRef.current;
      if (!overlay) return;

      if (animating.current) {
        if (!fast) return;
        tlRef.current?.kill();
      }
      animating.current = true;

      const tl = gsap
        .timeline({
          onComplete: () => {
            resetClosed();
            setIsOpen(false);
            setOpenSection(null);
            animating.current = false;
          },
        })
        .to(
          getItems(overlay),
          { yPercent: -100, duration: 0.5, stagger: 0.04, ease: "power3.in" },
          0
        )
        .to(closeIconRef.current, { yPercent: -100, duration: 0.6, ease: EASE }, 0)
        .to(openIconRef.current, { yPercent: 0, duration: 0.6, ease: EASE }, 0.15)
        .to(overlay, { yPercent: -100, duration: 0.8, ease: EASE }, 0.25);

      if (fast) tl.timeScale(1.8);
      tlRef.current = tl;
    },
    [resetClosed]
  );

  const handleToggleClick = () => {
    if (isOpen) handleCloseMenu();
    else handleOpenMenu();
  };

  const handleLinkClick = useCallback(() => handleCloseMenu(true), [handleCloseMenu]);

  const handleToggleSection = useCallback((label: string) => {
    setOpenSection((prev) => (prev === label ? null : label));
  }, []);

  // Lock page scroll while the menu is open
  useEffect(() => {
    document.documentElement.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [isOpen]);

  // Close with Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCloseMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleCloseMenu]);

  // Reset instantly if the viewport grows to desktop (2xl = 1024px)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => {
      if (!e.matches) return;
      tlRef.current?.kill();
      resetClosed();
      animating.current = false;
      setIsOpen(false);
      setOpenSection(null);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [resetClosed]);

  return (
    <>
      {/* Sits above the overlay (z-[3] > z-[2]) so the toggle is never covered */}
      <nav className="relative z-[3] flex items-center justify-end 2xl:hidden">
        <div className="flex items-center gap-2 sm:gap-4">
          <Letstalk />
          <button
            type="button"
            onClick={handleToggleClick}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            className="relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden"
          >
            <span
              ref={openIconRef}
              className="absolute inset-0 flex select-none items-center justify-center text-[var(--copy)]"
            >
              <RiMenu3Line size={24} />
            </span>
            {/* invisible until JS positions it, so it never overlaps the open icon */}
            <span
              ref={closeIconRef}
              className="invisible absolute inset-0 flex select-none items-center justify-center text-[var(--copy)]"
            >
              <RiCloseLine size={24} />
            </span>
          </button>
        </div>
      </nav>

      {/* invisible = no painting, no hit-testing, no tab focus while closed */}
      <div
        ref={overlayRef}
        className="invisible fixed inset-0 z-[2] flex items-center justify-center bg-secondary/95 2xl:hidden"
      >
        <MenuList
          openSection={openSection}
          onToggleSection={handleToggleSection}
          onLinkClick={handleLinkClick}
        />
      </div>
    </>
  );
};

export default Nav;